import { inject } from '@angular/core';
import { ActivatedRouteSnapshot, Router, RouterStateSnapshot, UrlTree } from '@angular/router';
import type { CanActivateFn } from '@angular/router';

import { AuthGuardData, createAuthGuard } from 'keycloak-angular';

import { AuthService } from '../services/auth/auth.service';
import { LoggerService } from '../services/logger/logger.service';

const LOG_OPTIONS = { when: ['debugGuards'] as const };

const isAccessAllowed = async (
  route: ActivatedRouteSnapshot,
  state: RouterStateSnapshot,
  authData: AuthGuardData
): Promise<boolean | UrlTree> => {
  const { authenticated, grantedRoles, keycloak } = authData;
  const router = inject(Router);
  const authService = inject(AuthService);
  const logger = inject(LoggerService);

  console.log('[AuthGuard] ========== ACCESS CHECK ==========');
  console.log('[AuthGuard] URL:', state.url);
  console.log('[AuthGuard] authenticated:', authenticated);
  console.log('[AuthGuard] authService.authError():', authService.authError());
  console.log('[AuthGuard] keycloak.authenticated:', keycloak.authenticated);
  console.log('[AuthGuard] keycloak.token:', keycloak.token ? 'present' : 'missing');

  logger.log('[AuthGuard] Checking access for url:', state.url, LOG_OPTIONS);

  if (authService.authError()) {
    console.log('[AuthGuard] ❌ Auth error detected, redirecting to /exception');
    logger.log('[AuthGuard] Auth error detected, redirecting to exception', LOG_OPTIONS);
    return router.parseUrl('/exception');
  }

  if (!authenticated) {
    console.log('[AuthGuard] ❌ Not authenticated, triggering Keycloak login');
    console.log('[AuthGuard] Redirect URI will be:', window.location.origin + state.url);
    logger.log('[AuthGuard] Not authenticated, redirecting to login', LOG_OPTIONS);
    await keycloak.login({
      redirectUri: window.location.origin + state.url
    });
    return false;
  }

  const requiredRoles = route.data['roles'] as string[] | undefined;
  if (!requiredRoles || requiredRoles.length === 0) {
    console.log('[AuthGuard] ✅ No roles required, access granted');
    logger.log('[AuthGuard] No roles required, access granted', LOG_OPTIONS);
    return true;
  }

  console.log('[AuthGuard] Required roles:', requiredRoles);
  console.log('[AuthGuard] Granted realm roles:', grantedRoles.realmRoles);
  console.log('[AuthGuard] Granted resource roles:', grantedRoles.resourceRoles);
  logger.log('[AuthGuard] Required roles:', requiredRoles, LOG_OPTIONS);


  const hasRequiredRoles = requiredRoles.every(role => {
    if (grantedRoles.realmRoles.includes(role)) {
      return true;
    }
    return Object.values(grantedRoles.resourceRoles).some(roles => roles.includes(role));
  });

  if (!hasRequiredRoles) {
    console.log('[AuthGuard] ❌ Missing required roles, redirecting to /forbidden');
    logger.log('[AuthGuard] Missing required roles, redirecting to forbidden', LOG_OPTIONS);
    return router.parseUrl('/forbidden');
  }

  console.log('[AuthGuard] ✅ All roles satisfied, access granted');
  logger.log('[AuthGuard] Access granted', LOG_OPTIONS);
  return true;
};

export const authGuard = createAuthGuard<CanActivateFn>(isAccessAllowed);
